import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import { memberAPI } from '../../services/api';
import { cn } from '../../lib/utils';

/**
 * LastMinuteSettings - Manage last-minute invite preferences
 * Opt-in/out, notification email, advance notice preferences
 */
const LastMinuteSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    acceptInvites: false,
    notificationEmail: '',
    maxAdvanceNotice: 24,
  });

  const [memberEmail, setMemberEmail] = useState('');
  const [inviteStats, setInviteStats] = useState({
    totalInvites: 0,
    acceptedInvites: 0,
    declinedInvites: 0,
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchSettings = async () => {
      if (!memberId) {
        setError('No member ID found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await memberAPI.getPortalSummary(memberId);
        const member = response.data.data.member;
        const prefs = member.lastMinuteInvitePreferences || {};

        setMemberEmail(member.email || '');
        setFormData({
          acceptInvites: prefs.acceptInvites ?? false,
          notificationEmail: prefs.notificationEmail || '',
          maxAdvanceNotice: prefs.maxAdvanceNotice || 24,
        });
        setInviteStats(prefs.inviteStats || {
          totalInvites: 0,
          acceptedInvites: 0,
          declinedInvites: 0,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
        setError('Failed to load your settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [memberId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await memberAPI.updateLastMinute(memberId, formData);
      setSuccess('Settings saved successfully!');
      setTimeout(() => {
        navigate('/portal');
      }, 1500);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const advanceNoticeOptions = [
    { value: 6, label: '6 hours', description: 'Day-of notifications only' },
    { value: 12, label: '12 hours', description: 'Morning or evening before' },
    { value: 24, label: '24 hours', description: 'Up to a day in advance' },
    { value: 48, label: '48 hours', description: 'Up to 2 days in advance' },
    { value: 72, label: '72 hours', description: 'Up to 3 days in advance' },
  ];

  if (loading) {
    return (
      <PortalLayout title="Last Min" showBack>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Last Min" showBack>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="bg-white/20 rounded-full p-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold">Last-Minute Invites</h2>
              <p className="text-yellow-100 mt-1">
                Get notified when spots open up due to cancellations or no-shows.
              </p>
            </div>
          </div>
        </div>

        {/* Opt-in Toggle */}
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Accept Last-Minute Invites</h3>
              <p className="text-sm text-gray-500 mt-1">
                We'll email you when spots become available
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, acceptInvites: !prev.acceptInvites }))}
              className={cn(
                'relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                formData.acceptInvites ? 'bg-green-600' : 'bg-gray-200'
              )}
              role="switch"
              aria-checked={formData.acceptInvites}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  formData.acceptInvites ? 'translate-x-6' : 'translate-x-0'
                )}
              />
            </button>
          </div>
        </div>

        {/* Settings (only show if opted in) */}
        {formData.acceptInvites && (
          <>
            {/* Notification Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Notification Email
              </label>
              <input
                type="email"
                value={formData.notificationEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, notificationEmail: e.target.value }))}
                placeholder={memberEmail || 'your@email.com'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              />
              <p className="text-xs text-gray-500">
                Leave blank to use your default email: {memberEmail || '(not set)'}
              </p>
            </div>

            {/* Advance Notice */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                How last-minute is too last-minute?
              </label>
              <p className="text-sm text-gray-500">
                Only notify me if the spot opens up at least this much in advance:
              </p>

              <div className="space-y-2">
                {advanceNoticeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, maxAdvanceNotice: option.value }))}
                    className={cn(
                      'w-full p-4 rounded-lg border-2 text-left transition-all',
                      formData.maxAdvanceNotice === option.value
                        ? 'border-green-500 bg-green-50 ring-2 ring-green-500 ring-opacity-50'
                        : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-500">{option.description}</div>
                      </div>
                      {formData.maxAdvanceNotice === option.value && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Stats (if has any history) */}
        {(inviteStats.totalInvites > 0) && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Your Invite History</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{inviteStats.totalInvites}</div>
                <div className="text-xs text-gray-500">Received</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{inviteStats.acceptedInvites}</div>
                <div className="text-xs text-gray-500">Accepted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-400">{inviteStats.declinedInvites}</div>
                <div className="text-xs text-gray-500">Declined</div>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saving}
          className={cn(
            'w-full py-4 rounded-xl font-medium text-lg transition-colors',
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          )}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Saving...
            </span>
          ) : (
            'Save Settings'
          )}
        </button>
      </form>
    </PortalLayout>
  );
};

export default LastMinuteSettings;
